import { keepPreviousData } from "@tanstack/react-query";
import find from "lodash/find";
import some from "lodash/some";
import { SidePanelType, useSidePanel } from "PFApp/stores/side_panels_store";
import { Stack } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import SidePanel from "PFComponents/side_panel/side_panel";
import { useKPIs } from "PFCore/hooks/queries/kpi";
import { useKPIResult } from "PFCore/hooks/queries/kpi/use_kpi_result";
import { useSynchronizedState } from "PFCore/hooks/use_synchronized_state";
import { KPIStatus } from "PFCore/types/kpi";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { KPICard } from "../kpi_card";
import css from "./kpis_review_sidepanel.module.scss";
import { KPIsReviewSidepanelData } from "./kpis_review_sidepanel.types";
import { KPIEventsList } from "./parts/kpi_events_list";
import { KPIsReviewFooter } from "./parts/kpis_review_footer";
import { useKPIsReviewSidepanelActions } from "./use_kpis_review_sidepanel_actions";

const COMPLIANT_STATUSES = [KPIStatus.OnTarget, KPIStatus.ApprovalGranted];

const KPIsReviewSidepanelContent = () => {
  const { t } = useTranslation("activities", { keyPrefix: "KPIsReviewSidepanel" });
  const { isOpen, index, isClosing, data } = useSidePanel<KPIsReviewSidepanelData>(SidePanelType.KPIsReview);
  const { closeKPIsReviewSidepanel } = useKPIsReviewSidepanelActions();
  const { KPIs, engagementName, initialKPIId, engagementId } = data;
  const initialKPIStatus = find(KPIs, ["indicatorId", initialKPIId])?.status;
  const isAnyNotCompliant = some(KPIs, ({ status }) => !COMPLIANT_STATUSES.includes(status));
  const isOnlyExceptionsInitially =
    isAnyNotCompliant && (!initialKPIStatus || !COMPLIANT_STATUSES.includes(initialKPIStatus));

  const [onlyExceptions, setOnlyExceptions] = useSynchronizedState(isOnlyExceptionsInitially);

  const visibleKPIs = useMemo(
    () => (onlyExceptions ? KPIs.filter(({ status }) => !COMPLIANT_STATUSES.includes(status)) : KPIs),
    [KPIs, onlyExceptions]
  );

  const [eventsCount, setEventsCount] = useState(0);
  const [currentKPIId, setCurrentKPIId] = useSynchronizedState(initialKPIId ?? visibleKPIs[0].indicatorId);

  const { data: currentKPIResult, isFetching: isFetchingResult } = useKPIResult(currentKPIId, engagementId, {
    placeholderData: keepPreviousData
  });
  const { data: currentKPIIndicator, isFetching: isFetchingIndicator } = useKPIs({
    select: (data) => data.entries.find((indicator) => indicator.id === currentKPIResult?.indicatorId),
    placeholderData: keepPreviousData
  });

  const changeCurrentKPI = useCallback(
    (newIndex: number) => {
      const newKPIId = visibleKPIs[newIndex].indicatorId;
      setCurrentKPIId(newKPIId);
    },
    [setCurrentKPIId, visibleKPIs]
  );

  const handleSetOnlyExceptions = useCallback(
    (toggleValue: boolean) => {
      setOnlyExceptions(toggleValue);
      const currentKPIStatus = find(visibleKPIs, ["indicatorId", currentKPIId])?.status;
      if (toggleValue && (!currentKPIStatus || COMPLIANT_STATUSES.includes(currentKPIStatus))) {
        // The current KPI won't be visible, so we need to change current KPI to the first visible one
        const onlyExceptionsKPIs = KPIs.filter(({ status }) => !COMPLIANT_STATUSES.includes(status));
        setCurrentKPIId(onlyExceptionsKPIs[0].indicatorId);
      }
    },
    [KPIs, currentKPIId, setCurrentKPIId, visibleKPIs]
  );

  const footerRenderer = useCallback(() => {
    const currentIndex = visibleKPIs.findIndex(({ indicatorId }) => indicatorId === currentKPIId);

    return (
      <KPIsReviewFooter
        currentIndex={currentIndex}
        totalCount={visibleKPIs.length}
        onlyExceptions={onlyExceptions}
        isAnyNotCompliant={isAnyNotCompliant}
        setOnlyExceptions={handleSetOnlyExceptions}
        onPrevClick={() => changeCurrentKPI(currentIndex - 1)}
        onNextClick={() => changeCurrentKPI(currentIndex + 1)}
      />
    );
  }, [
    visibleKPIs,
    onlyExceptions,
    isAnyNotCompliant,
    currentKPIId,
    handleSetOnlyExceptions,
    changeCurrentKPI
  ]);

  const isFetching = isFetchingResult || isFetchingIndicator;
  const isKPILoaded = currentKPIIndicator && currentKPIResult;

  return (
    <SidePanel
      fullHeight
      show={isOpen}
      zIndex={index}
      isSidePanelClosing={isClosing}
      title={engagementName}
      subTitle={t("KPIs")}
      onClose={closeKPIsReviewSidepanel}
      footerRenderer={footerRenderer}
      footerShadowRecalculateTrigger={eventsCount}
      size="medium"
    >
      <Stack gap="spacingLg">
        {isFetching && <LoadingDots circlesEnabled circleSize="sm" centered className={css.loading} />}
        {isKPILoaded && <KPICard kpiResult={currentKPIResult} isReadOnly />}
        <KPIEventsList
          engagementId={engagementId}
          KPIId={currentKPIId}
          KPIName={currentKPIIndicator?.name}
          KPIUnit={currentKPIIndicator?.unit}
          KPIStatus={currentKPIResult?.status}
          hideLoader={isFetching}
          onEventsCountChange={setEventsCount}
        />
      </Stack>
    </SidePanel>
  );
};

export const KPIsReviewSidepanel = () => {
  const { isOpen } = useSidePanel<KPIsReviewSidepanelData>(SidePanelType.KPIsReview);

  if (!isOpen) {
    // Avoid query calls and undefined data when sidepanel is not open
    return null;
  }

  return <KPIsReviewSidepanelContent />;
};
